import * as d3 from "d3";
import * as React from "react";

import { baseColors } from "./../styles";
import { PiePieceData } from "./piePieceData";
import { createArc, createExplodePiece } from "./utils";

interface PieLegendProps {
    data: PiePieceData[];
    innerRadius: number;
    offset?: number;
    outerRadius: number;
    onClick: (data: PiePieceData) => void;
    animate?: boolean;
    format?: (data: PiePieceData, index: number) => string;
    className?: string;
    colors?: d3.ScaleOrdinal<string, string>;
    width: number;
}

export const PieLegend = (props: PieLegendProps) => {
    const {
        offset = 0.5,
        outerRadius,
        innerRadius,
        animate,
        format,
        onClick = () => {
            /* noop */
        },
        className,
        colors = d3.scaleOrdinal(d3.schemeTableau10),
    } = props;

    const ref = React.useRef(null);

    const formatFn = React.useCallback(
        (data: PiePieceData, index: number) => {
            if (format === undefined) {
                if (data.value === undefined || data.value === null)
                    return data.label;
                return `${data.label}`;
            } else {
                return format(data, index);
            }
        },
        [format]
    );

    const colorFn = React.useCallback(
        (data: PiePieceData, index: number) => {
            if (!!data.color) return data.color;
            return colors(index.toString());
        },
        [colors]
    );

    const attachAnimationListeners = React.useCallback(
        (selection: d3.Selection<any, PiePieceData, any, unknown>) => {
            if (animate) {
                selection
                    .on("mouseover", function (d: any) {
                        d3.select(`[data-label="${d.id}-dot"]`)
                            .transition()
                            .ease(d3.easeQuadOut)
                            .attr("r", 10);
                        d3.select(`[data-label="${d.id}-pie"]`)
                            .transition()
                            .ease(d3.easeQuadOut)
                            .attr(
                                "d",
                                createExplodePiece(
                                    innerRadius,
                                    outerRadius
                                ) as any
                            );
                    })
                    .on("mouseout", function (d: any) {
                        d3.select(`[data-label="${d.id}-dot"]`)
                            .transition()
                            .ease(d3.easeQuadOut)
                            .attr("r", 7);
                        d3.select(`[data-label="${d.id}-pie"]`)
                            .transition()
                            .ease(d3.easeQuadIn)
                            .attr(
                                "d",
                                createArc(
                                    innerRadius,
                                    outerRadius,
                                    offset
                                ) as any
                            );
                    });
            }
            selection.on("click", function (d: any) {
                onClick?.(d);
            });
        },
        [innerRadius, outerRadius, offset, onClick, animate]
    );

    const legendOffset = 32;
    const countOffsetFromCircle = 16;
    const labelOffsetFromCircle = 56;

    React.useEffect(() => {
        const legend = d3.select(ref.current);
        const legendGroupWithData = legend
            .selectAll("g.legend")
            .data(props.data);
        legendGroupWithData.exit().remove();
        const legendGroupWithUpdate = legendGroupWithData
            .enter()
            .append("g")
            .attr("class", "legend");

        const circle = legendGroupWithUpdate
            .append("circle")
            .merge(legendGroupWithData.select("circle.element"))
            .attr("class", "element")
            .attr("data-label", (d, i) => `${d.id}-dot`)
            .attr("cx", 0)
            .attr("cy", function (d, i) {
                return 0 + i * legendOffset;
            })
            .attr("r", 7)
            .style("fill", colorFn)
            .style("alignment-baseline", "middle");

        attachAnimationListeners(circle);

        const label = legendGroupWithUpdate
            .append("text")
            .merge(legendGroupWithData.select("text.element_count"))
            .attr("x", 0 + countOffsetFromCircle)
            .attr("y", (d, i) => 0 + i * legendOffset)
            .append("tspan")
            .attr("class", "element_count")
            .style("fill", baseColors.black)
            .style("font-size", "20px")
            .text((data: PiePieceData, index: number) => data.value)
            .attr("text-anchor", "right")
            .style("alignment-baseline", "middle")
            .append("tspan")
            .attr("class", "element")
            .attr("dx", 3)
            .attr("dy", 0)
            .style("fill", baseColors.gray)
            .style("font-size", "14px")
            .text((data: PiePieceData, index: number) => data.label)
            .attr("text-anchor", "left")
            .style("alignment-baseline", "middle");

        attachAnimationListeners(label);
    }, [props.data, colors, attachAnimationListeners, formatFn, colorFn]);

    return (
        <svg className={className} width={props.width}>
            <g ref={ref} transform={"translate(20, 20)"} />
        </svg>
    );
};
